import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { QuestionCircleOutlined } from '@ant-design/icons';
import { Modal, Form, Input, Tooltip, Button } from 'antd';

import { ITableState } from '../../../../../app/providers/redux/slices/reports/table/types';
import { setCookie } from '../../../../../helpers/utils/cookies';
import {
  generateUniqueID,
} from '../../../../../helpers/utils/functions';
import {
  getLast7DaysPeriod,
} from '../../../../../helpers/utils/reports';
import {
  calculateWidth,
  convertToGridUnits,
  measureTextDimensions,
  showErrorMessage,
} from '../../../../../helpers/utils/ui';
import { normalizeMetricRefs } from '../../../../../shared/common/components/reports/metrics/utils';
import ModalSettings from '../../../../../shared/common/components/reports/ModalSettings';
import { ReportTable } from '../../../../../shared/common/components/reports/report-table/ReportTable';
import { chartDetails } from '../../../../../shared/common/constants/details';
import { useActions } from '../../../../../shared/common/hooks/useActions';
import { DashboardBlockProvider } from '../../../context';
import { DashboardComponent, BlockVisible, BlockCopyData, ColorRule, BLOCK_MIN_SIZES, GRID_CONSTANTS } from '../../../types';
import { useBlockModalState } from '../../../hooks/useBlockModalState';
import Params from '../../Params/Params';
import { BlockPreview } from '../components/BlockPreview';
import { BlockSettingsPanel } from '../components/BlockSettingsPanel';
import ColorRulesEditor from '../components/ColorRulesEditor';
import { HeadingStyleEditor } from '../components/HeadingStyleEditor';
import {
  attrItems,
  getReportTypeFromDate,
  chartToTableReportType,
} from '../constants';

interface Props {
  blockType: string;
  visible: boolean;
  setVisible: React.Dispatch<BlockVisible>;
  onAddBlock: (component: DashboardComponent) => void;
  copyData?: BlockCopyData;
}

interface AddBlockParams {
  metricsChecked: ITableState['metricsChecked'];
  groupsChecked: ITableState['groupsChecked'];
  selectedMetrics: ITableState['metricsChecked'];
  selectedGroupings: ITableState['groupsChecked'];
  details: string;
  attribution: {
    attribution: string[];
    type_applications: string[];
  }
}

const AddBlockModal: React.FC<Props> = ({
  visible,
  blockType,
  setVisible,
  onAddBlock,
  copyData,
}) => {
  const [ form ] = Form.useForm();
  const blockId = useMemo(() => generateUniqueID(), []);

  // Общий хук для управления состоянием модального окна
  const {
    // Redux state
    details,
    filters,
    filterParent,
    attributes,
    metricsChecked,
    groupsChecked,
    totalChecked,
    percentages,
    sparklines,
    cellFills,
    analysis,
    cellFilling,
    filtersTable,
    tableData,
    sortTable,
    chartType,
    isAxesVisible,

    // Режим выбора строк
    groupsSelectionMode,
    selectFirstNRows,

    // Локальное состояние
    extraFieldSuffix,
    currentSelect,
    setCurrentSelect,
    selectedMetrics,
    setSelectedMetrics,
    selectedGroupings,
    setSelectedGroupings,
    dataItemsMetrics,
    dataItemsGroupings,
    listKeysMetrics,
    listKeysGroupings,
    heading,
    setHeading,
    headingStyles,
    setHeadingStyles,
    isBasicMetricsVisible,
    setIsBasicMetricsVisible,
    isBasicGroupingsVisible,
    setIsBasicGroupingsVisible,
    isBlockVisible,
    setIsBlockVisible,
    filtersFilterList,

    // Обработчики
    handlePeriodDateChange,
    handleValueChange,
    handleListChange,
    handleCloseModal,
    applyTableCellSettings,

    // Redux actions
    actions: {
      setGroupings,
      setMetrics,
      setMetricsChecked,
      setGroupingsChecked,
      setFilters,
      setFilterParent,
      setAttributes,
      setDetails,
      setChartType,
      setTotalChecked,
      setMetaModalTableFetching,
      setMetaModalChartFetching,
      setPercentages,
      setTableData,
      setIsNotParamsFilterVisible,
      setGroupsSelectionMode,
      setSelectFirstNRows,
      setAxesVisibleValue,
    },
  } = useBlockModalState({
    form,
    mode: { type: copyData ? 'copy' : 'add', initialData: copyData },
    visible,
  });

  // ==================== УНИКАЛЬНАЯ ЛОГИКА ADD ====================

  // Порядок как в оригинале: сначала Redux cleanup, потом setVisible (закрытие)
  const handleClose = useCallback(() => {
    handleCloseModal();
    setVisible({ visible: false, blockType: '' });
  }, [ handleCloseModal, setVisible ]);

  const {
    setAttributesDashboardBlocks,
    setDashboardBlockDetails,
    setDashboardsBlocksInitialState,
    setDashboardsBlocksTableInitialState,
    setDashboardsBlocksChartState,
  } = useActions();

  // Места в панели «Группировки / Метрики», куда таблица дорисует кнопки, как в отчёте
  const [ toolbarLeftSlot, setToolbarLeftSlot ] = useState<HTMLElement | null>(null);
  const [ toolbarRightSlot, setToolbarRightSlot ] = useState<HTMLElement | null>(null);

  // Правила условной раскраски числа
  const [ colorRules, setColorRules ] = useState<ColorRule[]>([]);

  const generateAddBlockState = useCallback(({ metricsChecked, groupsChecked, selectedMetrics, selectedGroupings, details, attribution }: AddBlockParams) => {
    const newBlockId = Date.now().toString();
    const attributionValue =
      typeof attribution?.attribution === 'string'
        ? [ attribution.attribution ]
        : attribution?.attribution;
    const typeApplicationsValue =
      typeof attribution?.type_applications === 'string'
        ? [ attribution.type_applications ]
        : attribution?.type_applications;

    setAttributesDashboardBlocks({
      blockId: blockId,
      attribution: attributionValue,
      type_applications: typeApplicationsValue,
    } as any);

    setDashboardsBlocksInitialState([ blockId ]);
    setDashboardsBlocksTableInitialState({
      ids: [ blockId ],
      data: {
        groupings: selectedGroupings ?? [],
        metrics: selectedMetrics ?? [],
        metricsChecked,
        groupsChecked,
        totalChecked,
        percentages,
        sparklines,
        cellFills,
        analysis,
        cellFilling,
        filtersTable,
      },
    });
    setFilters({ data: [], blockId: blockId });

    setDashboardsBlocksChartState({
      ids: [ blockId ],
      data: {
        chartType: 'line',
        isAxesVisible: false,
      },
    });
    setDashboardBlockDetails({
      blockId: blockId,
      detail: details,
    });

    return {
      id: newBlockId,
      visible: true,
      data: {
        title: '',
        componentType: '',
        layoutData: {
          w: 40,
          h: 20,
          x: 0,
          y: 0,
          i: newBlockId,
          scaled: true,
          minW: GRID_CONSTANTS.MIN_BLOCK_WIDTH,
          minH: GRID_CONSTANTS.MIN_BLOCK_HEIGHT,
          moved: false,
          static: false
        },
        data: {
          blockId,
          selectedMetrics,
          metricsChecked,
          groupsChecked,
          selectedGroupings,
          heading: '',
          title: '',
          date: 'reg',
          details,
          filters: [],
          attribution,
          chartType: 'line',
          isAxesVisible: false,
        }
      }
    };
  }, [ totalChecked, groupsChecked ]);

  useLayoutEffect(() => {
    const initState = {
      metricsChecked,
      groupsChecked,
      selectedGroupings,
      selectedMetrics,
      details,
      attribution: attributes
    };
    generateAddBlockState(initState);
  }, []);

  // Пропсы для CartNumber в предпросмотре
  const cartNumberProps = useMemo(() => ({
    selectedMetrics,
    selectedGroupings,
    metricsChecked,
    attribution: attributes,
    filters,
    chartType,
    groupsChecked,
    isShow: true,
    generateBlockId: blockId,
    details,
    tableData,
    colorRules,
  }), [
    selectedMetrics,
    selectedGroupings,
    metricsChecked,
    attributes,
    filters,
    chartType,
    groupsChecked,
    blockId,
    details,
    tableData,
    colorRules,
  ]);

  // Сброс видимости блока при изменении параметров предпросмотра
  useEffect(() => {
    if (isBlockVisible) {
      setIsBlockVisible(false);
    }
  }, [currentSelect, heading, headingStyles, cartNumberProps]);

  // Рендер предпросмотра через общий компонент
  const renderView = useMemo(() => {
    return (
      <BlockPreview
        displayType={currentSelect as any}
        form={form}
        heading={heading}
        headingStyles={headingStyles}
        chartHeight={350}
        maxRows={10}
        cartNumberProps={cartNumberProps}
      />
    );
  }, [
    currentSelect,
    form,
    heading,
    headingStyles,
    cartNumberProps,
  ]);

  const onSubmit = (values: any) => {
    const displayValue = form.getFieldValue('display');

    const { attribution, type_applications } = attributes;
    const newBlockId = blockId;
    const attributionValue =
      typeof attribution === 'string' ? [ attribution ] : attribution;
    const typeApplicationsValue =
      typeof type_applications === 'string'
        ? [ type_applications ]
        : type_applications;

    const chartData = {
      details,
      filters: filters,
      filterParent: filterParent,
      attribution: {
        attribution: attributionValue,
        type_applications: typeApplicationsValue,
      },
      chartType,
      isAxesVisible,
    };
    // «Динамика» и заливка всей таблицы — только у блока-таблицы: у графиков скрытая таблица
    // иначе делала бы лишние запросы прошлого периода
    const isTableBlock = (displayValue || blockType) === 'table';
    const blockAnalysis = isTableBlock && analysis === true;
    const blockCellFilling = isTableBlock && cellFilling === true;
    const isChosenNumberMetric = (displayValue || blockType) === 'number';
    if (isChosenNumberMetric && metricsChecked.length === 0) {
      showErrorMessage('Выберите метрику в таблице.');
      return;
    }

    setAttributesDashboardBlocks({
      blockId: newBlockId,
      attribution: attributes?.attribution,
      type_applications: attributes?.type_applications,
    });

    setDashboardsBlocksInitialState([ newBlockId ]);
    setDashboardsBlocksTableInitialState({
      ids: [ newBlockId ],
      data: {
        groupings: selectedGroupings,
        metrics: selectedMetrics,
        metricsChecked,
        groupsChecked: groupsSelectionMode !== 'manual' ? [] : groupsChecked,
        totalChecked,
        percentages,
        sparklines,
        cellFills,
        analysis: blockAnalysis,
        cellFilling: blockCellFilling,
        filtersTable,
        sortTable,
        groupsSelectionMode: groupsSelectionMode || 'manual',
        selectFirstNRows: selectFirstNRows ?? null,
      },
    });
    setFilters({ data: filters, blockId: newBlockId });

    setDashboardsBlocksChartState({
      ids: [ newBlockId ],
      data: {
        chartType,
        isAxesVisible,
      },
    });
    setDashboardBlockDetails({
      blockId: newBlockId,
      detail: details,
    });
    const getHeight = () => {
      const blockTypeValue = displayValue || blockType;
      if (blockTypeValue === 'chart' && chartType === 'pie') {
        return 20;
      }
      if (blockTypeValue === 'number') {
        return 4;
      }
      return 20;
    };
    const getWidth = () => {
      const componentType = displayValue || blockType;
      if (componentType === 'number') {
        // Авторасчёт ширины по длине заголовка
        const title = values.title || '';
        const charWidth = 8; // ~8px на символ при font-size: 13px
        const blockPadding = 60; // padding + иконки drag/dropdown
        const titlePx = title.length * charWidth + blockPadding;
        const minValuePx = 190; // минимум для числового значения (до ~7 цифр при 32px bold + суффикс ₽/%)
        const requiredPx = Math.max(titlePx, minValuePx);
        const gridW = Math.ceil(requiredPx / GRID_CONSTANTS.CELL_WIDTH);
        return Math.max(gridW, BLOCK_MIN_SIZES.number.minW);
      }
      return calculateWidth(selectedMetrics.length, 12) * GRID_CONSTANTS.SCALE_X;
    };

    const blockMinSizes = BLOCK_MIN_SIZES[displayValue || blockType] || {
      minW: GRID_CONSTANTS.MIN_BLOCK_WIDTH,
      minH: GRID_CONSTANTS.MIN_BLOCK_HEIGHT,
    };

    const newComponent: DashboardComponent = {
      title: values.title,
      componentType: displayValue || blockType,
      layoutData: {
        i: newBlockId,
        x: 0,
        y: Infinity,
        scaled: true,
        minH: blockMinSizes.minH,
        minW: blockMinSizes.minW,
        ...(() => {
          if (blockType === 'heading') {
            const textDimensions = measureTextDimensions(values.title);
            const { w, h } = convertToGridUnits(
              textDimensions.width,
              textDimensions.height,
              140,
              10
            );
            return { w, h };
          }

          return {
            w: getWidth(),
            h: getHeight(),
          };
        })(),
      },
      data: {
        blockId: newBlockId,
        selectedMetrics,
        metricsChecked,
        groupsChecked: groupsSelectionMode !== 'manual' ? [] : groupsChecked,
        totalChecked,
        groupsSelectionMode: groupsSelectionMode || 'manual',
        selectFirstNRows: selectFirstNRows ?? null,
        selectedGroupings,
        groupings: selectedGroupings,
        metrics: selectedMetrics,
        sortTable,
        sparklines,
        cellFills,
        analysis: blockAnalysis,
        cellFilling: blockCellFilling,
        heading,
        headingStyles,
        colorRules: colorRules.length > 0 ? colorRules : undefined,
        ...values,
        date: form.getFieldValue('date'),
        ...chartData,
      },
    };
    onAddBlock(newComponent);
    setVisible({ visible: false, blockType: '' });
  };

  // Флаг для отслеживания инициализации формы
  const isInitializedRef = useRef(false);

  // Флаг режима копирования
  const isCopyMode = Boolean(copyData && blockType !== 'heading' && (copyData.selectedMetrics || copyData.selectedGroupings));

  // Инициализация формы при открытии модального окна
  useEffect(() => {
    if (visible && !isInitializedRef.current) {
      isInitializedRef.current = true;

      // Очищаем предыдущие данные таблицы
      setTableData({ rows: [], total: 0, groupings: [] });
      // Копия берёт «%», спарклайны, заливку и «Динамику» исходного блока, новый блок — пустые
      setPercentages(isCopyMode && Array.isArray(copyData?.percentages) ? copyData!.percentages : []);
      applyTableCellSettings(isCopyMode ? copyData : undefined);

      // Режим копирования heading-блока
      if (blockType === 'heading' && copyData) {
        if (copyData.title) {
          form.setFieldValue('title', copyData.title);
        }
        if (copyData.description) {
          form.setFieldValue('description', copyData.description);
        }
      // Режим копирования блока (chart/table/number)
      } else if (isCopyMode && copyData) {
        const initCopyData = async () => {
          if (copyData.title) {
            form.setFieldValue('title', `${copyData.title} copy`);
          }
          if (copyData.description) {
            form.setFieldValue('description', copyData.description);
          }

          setFilters({ isModal: true, data: copyData.filters || [] });
          setFilterParent({ isModal: true, data: copyData.filterParent || [] });

          if (copyData.chartType) {
            setChartType(copyData.chartType);
          }
          if (copyData.isAxesVisible !== undefined) {
            setAxesVisibleValue(copyData.isAxesVisible);
          }
          if (copyData.attribution) {
            setAttributes(copyData.attribution as { [key: string]: [string] | string });
          }
          if (copyData.details) {
            setDetails(copyData.details as string);
          }

          const displayValue = blockType || 'chart';
          form.setFieldValue('display', displayValue);
          setCurrentSelect(displayValue);
          setCookie('chosenDisplay', displayValue, { expires: 300 });

          const dateValue = copyData.date || 'reg';
          form.setFieldValue('date', dateValue);

          await handlePeriodDateChange(dateValue, true, copyData);

          const savedSelectionMode = copyData.groupsSelectionMode || 'manual';
          setGroupsSelectionMode(savedSelectionMode);
          setSelectFirstNRows(copyData.selectFirstNRows ?? null);
          if (savedSelectionMode === 'manual' && copyData.groupsChecked) {
            setGroupingsChecked(copyData.groupsChecked);
          }
          if (copyData.metricsChecked) {
            setMetricsChecked({ data: normalizeMetricRefs(copyData.metricsChecked || []) });
          }
          if (copyData.colorRules) {
            setColorRules(copyData.colorRules);
          }
          if (displayValue === 'number' || displayValue === 'chart') {
            setTotalChecked();
          }

          setMetaModalTableFetching({ value: true });
        };

        initCopyData();
      } else {
        // Обычный режим добавления нового блока
        setFilters({ isModal: true, data: [] });
        setFilterParent({ isModal: true, data: [] });

        const currentDate = form.getFieldValue('date');
        const currentDisplay = form.getFieldValue('display');

        const displayValue = currentDisplay || blockType || 'chart';
        if (!currentDisplay) {
          form.setFieldValue('display', displayValue);
          setCurrentSelect(displayValue);
        }

        if (!currentDate) {
          handlePeriodDateChange('reg').then(() => {
            setMetaModalTableFetching({ value: true });
            if (displayValue === 'number') {
              setTotalChecked();
            }
          });
        } else if (displayValue === 'number') {
          setTotalChecked();
        }
      }
    }

    if (!visible) {
      isInitializedRef.current = false;
    }

    return () => {
      setPercentages([]);
      applyTableCellSettings();
      setGroupings({ data: [] });
      setMetrics({ data: [] });
      setGroupingsChecked([]);
      setMetricsChecked({ data: [] });
      setTableData({ rows: [], total: 0, groupings: [] });
      setIsNotParamsFilterVisible(false);
    };
  }, [ visible, copyData, isCopyMode ]);

  // проверяем пустой ли инпут
  const [ inputLength, setInputLength ] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const updateLength = () => {
      setInputLength(inputRef.current?.value.trim().length ?? 0);
    };

    inputRef.current?.addEventListener('input', updateLength);

    return () => inputRef.current?.removeEventListener('input', updateLength);
  }, []);

  const handleSave = () => {
    form.submit();
  };

  // ==================== RENDER ====================

  return (
    <Modal
      title={null}
      open={visible}
      className={'addBlockModal modal-settings'}
      width={1750}
      footer={null}
      maskClosable={false}
      onCancel={handleClose}
    >
      <DashboardBlockProvider
        mode="modal-add"
        componentType={currentSelect}
        date={form.getFieldValue('date')}
      >
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <div style={{ width: '98.5%' }}>
          <Form onFinish={onSubmit} layout={'vertical'} form={form}>
            <div
              style={{
                marginTop: '16px',
                width: '100%',
                display: 'flex',
                columnGap: '8px',
                alignItems: 'flex-end',
                marginBottom: blockType === 'heading' ? '20px' : '0',
              }}
            >
              <Form.Item
                name="title"
                label={
                  currentSelect === 'heading' ? 'Заголовок' : 'Название блока'
                }
                style={{ flex: blockType === 'heading' ? '1' : undefined, marginBottom: 0 }}
              >
                {blockType === 'heading' ? (
                  <Input
                    value={heading}
                    onChange={(event) => setHeading(event.target.value)}
                    style={{ fontSize: '16px', fontWeight: 500, height: '40px', borderRadius: '8px' }}
                  />
                ) : (
                  <div>
                    <input
                      id="inputTitle"
                      ref={inputRef}
                      style={{
                        border: '1px solid #dcdcdc',
                        height: '40px',
                        borderRadius: '8px',
                        marginBottom: '1px',
                        paddingLeft: '10px',
                        outline: 'none',
                        transition: 'box-shadow 0.2s ease-in-out',
                      }}
                    />
                  </div>
                )}
              </Form.Item>
              <Form.Item
                style={{ width: '60%', marginBottom: 0 }}
                name="description"
                label="Описание"
              >
                <Input style={{ height: '40px', borderRadius: '8px' }} />
              </Form.Item>
            </div>

            {blockType === 'heading' && (
              <HeadingStyleEditor
                styles={headingStyles}
                onStylesChange={setHeadingStyles}
                previewText={heading || 'Введите текст заголовка'}
              />
            )}
            {blockType !== 'heading' && (
              <>
                <BlockSettingsPanel
                  form={form}
                  currentSelect={currentSelect as any}
                  onValueChange={handleValueChange}
                />
              </>
            )}
            {currentSelect !== '' && currentSelect !== 'heading' && (
              <div className="dashboard-params-compact" style={{ marginBottom: '20px' }}>
                <Params
                  attributionItems={attrItems}
                  detailsItems={chartDetails}
                  filtersFilter={filtersFilterList}
                  isFilterVisible={false}
                  isModalDashboard={true}
                />
              </div>
            )}

            {(selectedMetrics.length > 0 || selectedGroupings.length > 0) && (currentSelect !== 'heading' && blockType !== 'heading') && (
              <>
                <div
                  className="universal-table-settings"
                  style={{ display: 'flex', justifyContent: 'space-between' }}
                >
                  <div style={{ display: 'flex', columnGap: '4px', alignItems: 'center' }}>
                    <Button data-help-id="block-groupings" onClick={() => setIsBasicGroupingsVisible(true)}>
                      Группировки
                    </Button>
                    <Tooltip
                      title={
                        <div
                          className="page-title-tooltip-text"
                          dangerouslySetInnerHTML={{
                            __html:
                              'Группировка — это возможность сгруппировать регистрации пользователей по определённым условиям и посмотреть их данные по 60 метрикам. В SCANе около 70 различных группировок. <a href="https://doc.scananalytics.ru/otchety/gruppirovki/" rel="noopener noreferrer" target="_blank">Подробнее</a>.',
                          }}
                        ></div>
                      }
                      color="#fff"
                      zIndex={9999}
                      trigger="click"
                      overlayClassName="page-title-tooltip"
                    >
                      <QuestionCircleOutlined
                        style={{ color: '#ccc', fontSize: 14, marginLeft: 10 }}
                      />
                    </Tooltip>
                    {/* Сюда таблица дорисует выгрузку */}
                    <span ref={setToolbarLeftSlot} style={{ display: 'flex', alignItems: 'center' }} />
                  </div>
                  <div style={{ display: 'flex', columnGap: '20px', alignItems: 'center' }}>
                    <Button data-help-id="block-metrics" onClick={() => setIsBasicMetricsVisible(true)}>
                      Метрики
                    </Button>
                    <Tooltip
                      placement="bottom"
                      title={
                        <div
                          className="page-title-tooltip-text"
                          dangerouslySetInnerHTML={{
                            __html:
                              'Метрики — это показатель выбранных группировок. В сервисе около 60 разных показателей. <a href="https://doc.scananalytics.ru/otchety/metriki/" rel="noopener noreferrer" target="_blank">Подробнее</a>.',
                          }}
                        ></div>
                      }
                      color="#fff"
                      zIndex={9999}
                      trigger="click"
                      overlayClassName="page-title-tooltip"
                    >
                      <QuestionCircleOutlined
                        style={{ color: '#ccc', fontSize: 14 }}
                      />
                    </Tooltip>
                    {/* Сюда таблица дорисует «Динамику», заливку и копирование */}
                    <span ref={setToolbarRightSlot} style={{ display: 'flex', alignItems: 'center' }} />
                  </div>
                </div>

                <ReportTable
                  reportType={chartToTableReportType(getReportTypeFromDate(form.getFieldValue('date')))}
                  displayMode="preview"
                  isModal={true}
                  date={form.getFieldValue('date')}
                  hideGroupingsPanel={true}
                  hideMetricsPanel={true}
                  hideSelectionControls={currentSelect === 'number' || currentSelect === 'table'}
                  hideTotalCheckbox={currentSelect === 'table'}
                  hideMetricButtons={currentSelect === 'table'}
                  disableGroupsLimit={currentSelect === 'table'}
                  overridePeriod={getLast7DaysPeriod()}
                  toolbarSlots={currentSelect === 'table'
                    ? { left: toolbarLeftSlot, right: toolbarRightSlot }
                    : undefined}
                  showTableSettings={currentSelect === 'table'}
                  getExportFileName={() => inputRef.current?.value || ''}
                />

                {currentSelect === 'number' && (
                  <ColorRulesEditor rules={colorRules} onChange={setColorRules} />
                )}
              </>
            )}

          </Form>
        </div>
        {currentSelect !== 'heading' && (
          <div
            style={{
              width: '100%',
              paddingRight: '20px',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: '#fff',
              flex: 1,
              paddingTop: '30px',
              flexDirection: 'column',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
                marginBottom: '20px',
                marginRight: '45px',
              }}
            >
              {(selectedMetrics.length > 0 || selectedGroupings.length > 0) && (
                <button
                  data-help-id="block-preview"
                  style={{
                    backgroundColor: '#11487d',
                    color: 'white',
                    width: '170px',
                    height: '45px',
                    borderRadius: '5px',
                    border: 'none',
                    cursor: 'pointer',
                  }}
                  onClick={() => {
                    setIsBlockVisible((prev) => !prev);
                    !isBlockVisible && setMetaModalChartFetching({ value: true });
                  }}
                >
                  {isBlockVisible ? 'Скрыть предпросмотр' : 'Предпросмотр'}
                </button>
              )}

              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  columnGap: '12px',
                }}
              >
                {(selectedMetrics.length > 0 || selectedGroupings.length > 0) && (() => {
                  const isNumberSelected = form.getFieldValue('display') === 'number';

                  return (
                    <>
                      {isNumberSelected ? (
                        <Tooltip title={inputLength === 0 ? 'Укажите название блока' : ''}>
                          <Button
                            data-help-id="block-save"
                            onClick={handleSave}
                            disabled={inputLength === 0}
                            style={{
                              backgroundColor: inputLength === 0 ? '#ccc' : '#fe3f30',
                              color: 'white',
                              width: '170px',
                              height: '45px',
                              borderRadius: '5px',
                              border: 'none',
                              cursor: inputLength === 0 ? 'not-allowed' : 'pointer',
                            }}
                          >
                            Сохранить и закрыть
                          </Button>
                        </Tooltip>
                      ) : (
                        <Button
                          data-help-id="block-save"
                          onClick={handleSave}
                          style={{
                            backgroundColor: '#fe3f30',
                            color: 'white',
                            width: '170px',
                            height: '45px',
                            borderRadius: '5px',
                            border: 'none',
                          }}
                        >
                          Сохранить и закрыть
                        </Button>
                      )}

                      <Button
                        data-help-id="block-close"
                        style={{
                          backgroundColor: 'white',
                          color: '#bfbfbf',
                          width: '170px',
                          height: '45px',
                          borderRadius: '5px',
                          border: '1px solid #bfbfbf',
                        }}
                        type="primary"
                        onClick={handleClose}
                        htmlType="submit"
                      >
                        Закрыть
                      </Button>
                    </>
                  );
                })()}
              </div>
            </div>

            {isBlockVisible && (
              <div
                style={{
                  textAlign: 'left',
                  marginTop: '20px',
                  background: '#f3f3f3',
                  borderRadius: '12px',
                  padding: currentSelect === 'number' ? '0' : '16px',
                  ...(currentSelect === 'table'
                    ? {
                      width: '100%',
                      paddingLeft: '24px',
                      background: '#f3f3f3',
                      borderRadius: '12px',
                    }
                    : {}),
                }}
              >
                <Form.Item>
                  <div style={{ maxHeight: '40%', overflowY: 'hidden' }}>
                    {renderView}
                  </div>
                </Form.Item>
              </div>
            )}

          </div>
        )}
        <div style={{ marginTop: '20px', marginBottom: '20px' }}>
          {blockType === 'heading' ? (
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <Button
                data-help-id="block-save"
                onClick={() => form.submit()}
                style={{
                  backgroundColor: '#fe3f30',
                  color: 'white',
                  width: '170px',
                  height: '45px',
                  borderRadius: '5px',
                  border: 'none',
                  cursor: 'pointer',
                }}
              >
                Добавить и закрыть
              </Button>
              <Button
                data-help-id="block-close"
                style={{
                  backgroundColor: 'white',
                  color: '#bfbfbf',
                  width: '170px',
                  height: '45px',
                  borderRadius: '5px',
                  border: '1px solid #bfbfbf',
                }}
                type="primary"
                onClick={handleClose}
              >
                Закрыть
              </Button>
            </div>
          ) : (
            <div></div>
          )}
        </div>

      </div>
      {isBasicMetricsVisible && (
        <ModalSettings
          title={'Метрики'}
          dataItems={dataItemsMetrics}
          listKeys={listKeysMetrics}
          defSelected={selectedMetrics}
          defChecked={selectedMetrics
            .map((m: any) => m?.dataKey ?? m?.key)
            .filter(Boolean)}
          actionSet={(newState) => {
            handleListChange(newState, setSelectedMetrics, setMetrics);
            setMetaModalTableFetching({ value: true });
            if (form.getFieldValue('display') === 'number' && newState.length > 0) {
              const metric = newState[0];
              setMetricsChecked({
                data: [ {
                  dataKey: metric?.dataKey || metric?.key,
                  name: metric?.dataKey || metric?.key,
                  label: metric?.name || metric?.label,
                } ]
              });
              setTotalChecked();
            }
          }}
          isVisible={isBasicMetricsVisible}
          setVisible={setIsBasicMetricsVisible}
          hideTriggerButton
          singleSelect={form.getFieldValue('display') === 'number'}
          isAdReport={form.getFieldValue('date') === 'ad'}
        />
      )}
      {isBasicGroupingsVisible && (
        <ModalSettings
          title={'Группировки'}
          dataItems={dataItemsGroupings}
          listKeys={listKeysGroupings}
          defSelected={selectedGroupings}
          defChecked={selectedGroupings
            .map((g: any) => g?.dataKey ?? g?.key)
            .filter(Boolean)}
          actionSet={(newState) => {
            handleListChange(newState, setSelectedGroupings, setGroupings);
            setMetaModalTableFetching({ value: true });
          }}
          isVisible={isBasicGroupingsVisible}
          setVisible={setIsBasicGroupingsVisible}
          hideTriggerButton
          extraFieldSuffix={extraFieldSuffix}
          isAdReport={form.getFieldValue('date') === 'ad'}
        />
      )}
      </DashboardBlockProvider>
    </Modal>
  );
};

export default AddBlockModal;
